<script setup lang="ts">
// System-admin dialog to set which regulators an account is obligated to report to.
// Multi-select (an account can answer to several); saves the chosen slugs.
import { ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import BaseIcon from '@/components/base/BaseIcon.vue';
import RegulatorPicker from '@/components/common/RegulatorPicker.vue';

import type { Account } from '@/types/account';
import type { Regulator } from '@/types/regulators';

const props = defineProps<{
  visible: boolean;
  account: Account | null;
  regulators: Regulator[]; // all available options (GET /regulators)
  saving?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'save', accountId: string, slugs: string[]): void;
}>();

const selected = ref<string[]>([]);

// Re-seed the selection whenever the dialog opens for an account.
watch(
  () => props.visible,
  (open) => {
    if (open && props.account) {
      selected.value = (props.account.regulators ?? []).map((r) => r.slug);
    }
  },
  { immediate: true },
);

const close = () => emit('update:visible', false);

const save = () => {
  if (!props.account) return;
  emit('save', props.account.id, selected.value);
};
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :draggable="false"
    :closable="!saving"
    class="w-full max-w-lg"
    @update:visible="(v) => !v && close()"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
          <BaseIcon size="sm">gavel</BaseIcon>
        </span>
        <div>
          <p class="font-bold text-slate-800">Reguladores</p>
          <p class="text-xs text-slate-400">{{ account?.name }}</p>
        </div>
      </div>
    </template>

    <div class="flex flex-col gap-2 pt-1">
      <label class="text-xs font-bold uppercase tracking-wide text-slate-400">
        Reguladores da conta
      </label>
      <RegulatorPicker v-model="selected" multiple :regulators="regulators" :disabled="saving" />
    </div>

    <template #footer>
      <Button label="Cancelar" text severity="secondary" :disabled="saving" @click="close" />
      <Button :label="saving ? 'Salvando…' : 'Salvar'" :disabled="saving" @click="save">
        <template #icon>
          <BaseIcon size="sm" class="mr-1" :class="{ 'animate-spin': saving }">{{ saving ? 'progress_activity' : 'save' }}</BaseIcon>
        </template>
      </Button>
    </template>
  </Dialog>
</template>
